import { hasOwnProperty } from "~/tools/has-own-property";
import { Lazy } from "~/tools/lazy";
import type { Record } from "~/tools/record";
import { type Observable, Subject } from "~/tools/rxjs";
import { toKeys } from "~/tools/to-keys";
import { toValues } from "~/tools/to-values";

/**
 * @deprecated avoid using this class - it is only used in a legacy context
 */
export type SparseArrayChangedEvent<TKey extends number, TValue> =
	| { type: "UPDATED"; values: Array<TValue> }
	| { type: "REMOVED"; keys: Array<TKey> };

/**
 * @deprecated avoid using this class - it is only used in a legacy context
 */
export class SparseArray<K extends number, T> {
	public static ofArray<K extends number, T>(
		array: Array<T>,
		keyExtractor: (element: T, idx?: number) => K,
	): SparseArray<K, T> {
		return new SparseArray<K, T>().mergeArray(array, keyExtractor);
	}

	public static empty<K extends number, T>(): SparseArray<K, T> {
		return new SparseArray<K, T>();
	}

	private dict: Record<string, T, "writable"> = {};

	private readonly _eventSource = Lazy.create(() => new Subject<SparseArrayChangedEvent<K, T>>());

	private readonly _eventSourceAsObservable = Lazy.create(() => this._eventSource.get().asObservable());

	private constructor() {}

	public mergeArray(array: Array<T>, keyExtractor: (element: T, idx?: number) => K): SparseArray<K, T> {
		if (array.length > 0) {
			this.mergeMany(array, (idx, value) => keyExtractor(value, idx));
		}

		return this;
	}

	public clear(): void {
		const keys = this.keys();
		this.dict = {};
		this._eventSource.tap((es) => es.next({ type: "REMOVED", keys }));
	}

	public remove(id: K): void {
		if (this.exists(id)) {
			delete this.dict[id];
			this._eventSource.tap((es) => es.next({ type: "REMOVED", keys: [id] }));
		}
	}

	public exists(id: K): boolean {
		return hasOwnProperty(this.dict, String(id));
	}

	public values(): Array<T> {
		return toValues(this.dict);
	}

	public keys(): Array<K> {
		return toKeys(this.dict).map((s) => Number(s) as K);
	}

	public merge(id: K, val: T): void {
		this.doMerge(id, val, true);
	}

	private doMerge(id: K, val: T, triggerEvent: boolean): void {
		this.dict[id] = val;
		if (triggerEvent) {
			this._eventSource.tap((es) => es.next({ type: "UPDATED", values: [val] }));
		}
	}

	private mergeMany(array: Array<T>, keys: (idx: number, value: T) => K) {
		for (const [index, val] of array.entries()) {
			this.doMerge(keys(index, val), val, false);
		}

		if (array.length > 0) {
			this._eventSource.tap((es) => es.next({ type: "UPDATED", values: array }));
		}
	}

	public get(id: K): T | undefined {
		return this.dict[id];
	}

	public eventStream(): Observable<SparseArrayChangedEvent<K, T>> {
		return this._eventSourceAsObservable.get();
	}
}
